import { useTranslation } from 'react-i18next'
import { Link, useLocation, useParams } from 'react-router-dom'
import { Home, CalendarPlus, Ticket, User, type LucideIcon } from 'lucide-react'
import { useAuth } from '../../store/authStore'
import { cn } from '../../lib/cn'

interface Tab {
  page: string // path segment after the locale ('' = home)
  to: string
  icon: LucideIcon
  label: string
}

/**
 * Mobile-only bottom tab bar (lg:hidden). Rendered by LocaleLayout and hidden on the funnel
 * steps (which have their own sticky action bar). Sits at z-30 — below the ticket modal (z-80+).
 */
export function MobileFooterNav() {
  const { t } = useTranslation()
  const { locale = 'it' } = useParams()
  const { pathname } = useLocation()
  const authed = useAuth((s) => s.status === 'authenticated')

  const current = pathname.split('/').filter(Boolean)[1] ?? ''

  const tabs: Tab[] = [
    { page: '', to: '', icon: Home, label: t('nav.home') },
    { page: 'book', to: 'book', icon: CalendarPlus, label: t('nav.book') },
    { page: 'bookings', to: 'bookings', icon: Ticket, label: t('nav.tickets') },
    authed
      ? { page: 'profile', to: 'profile', icon: User, label: t('nav.account') }
      : { page: 'login', to: 'login', icon: User, label: t('nav.account') },
  ]

  return (
    <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-ink-100 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden">
      <div className="mx-auto flex max-w-md items-stretch">
        {tabs.map((tab) => {
          const active = current === tab.page
          const to = `/${locale}${tab.to ? `/${tab.to}` : ''}`
          return (
            <Link
              key={tab.label}
              to={to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10.5px] font-semibold transition-colors',
                active ? 'text-brand-600' : 'text-ink-400 hover:text-ink-600',
              )}
            >
              <tab.icon className={cn('h-[22px] w-[22px]', active && 'stroke-[2.4]')} />
              {tab.label}
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
